import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { db, collection, onSnapshot, doc, updateDoc, deleteDoc, getDoc, deleteField, getDocs, query, where, writeBatch } from '../../firebase';
import { 
  Calendar, Clock, Edit, Trash2, Save, X, 
  ChevronLeft, ChevronRight, Filter, 
  CheckCircle, AlertCircle, Users, UserCheck,
  ChevronDown, ChevronUp, User, CalendarClock, Repeat, Check
} from 'lucide-react';
import ReservationDetailsModal from './ReservationDetailsModal';
import styles from '../CSS/MyReservations.module.css';

const MONTH_MAP = {
  Enero: 1, Febrero: 2, Marzo: 3, Abril: 4, Mayo: 5, Junio: 6,
  Julio: 7, Agosto: 8, Septiembre: 9, Octubre: 10, Noviembre: 11, Diciembre: 12
};

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const DAYS_OF_WEEK = [
  { value: 1, short: 'L', full: 'Lunes' },
  { value: 2, short: 'M', full: 'Martes' },
  { value: 3, short: 'X', full: 'Miércoles' },
  { value: 4, short: 'J', full: 'Jueves' },
  { value: 5, short: 'V', full: 'Viernes' }
];

// Generar opciones de tiempo cada 10 minutos
const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      options.push(`${hour}:${minute}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Función para filtrar opciones de hora fin basadas en hora inicio
const getEndTimeOptions = (startTime) => {
  if (!startTime) return TIME_OPTIONS;
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute;
  
  return TIME_OPTIONS.filter(time => {
    const [hour, minute] = time.split(':').map(Number);
    const totalMinutes = hour * 60 + minute;
    return totalMinutes > startTotalMinutes;
  });
};

// ============================================
// COMPONENTE: MODAL DE EDICIÓN
// ============================================
const EditReservationModal = ({ 
  reservation, 
  editForm, 
  onEditFormChange, 
  onSaveEdit, 
  onClose,
  conflictData,
  onDayToggle,
  onResolutionChange,
  canDelegateReservations
}) => {
  const isPeriodic = reservation.type === 'periodic';
  const [delegationMode, setDelegationMode] = useState('none');
  const [delegatedUser, setDelegatedUser] = useState(null);
  const [delegatedUserSearch, setDelegatedUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [manualDelegation, setManualDelegation] = useState({
    name: '',
    email: ''
  });

  // Inicializar estados de delegación
  useEffect(() => {
    if (reservation.delegatedTo) {
      if (reservation.delegatedTo.delegationType === 'database') {
        setDelegationMode('search');
        setDelegatedUser(reservation.delegatedTo);
        setDelegatedUserSearch(reservation.delegatedTo.displayName);
      } else if (reservation.delegatedTo.delegationType === 'manual') {
        setDelegationMode('manual');
        setManualDelegation({
          name: reservation.delegatedTo.displayName || '',
          email: reservation.delegatedTo.email || ''
        });
      }
    }
  }, [reservation]);

  // Función para buscar usuarios
  // Función para buscar usuarios
const searchUsers = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    setSearchResults([]);
    return;
  }

  setIsSearching(true);
  try {
    const usersRef = collection(db, 'users');
    const searchLower = searchTerm.toLowerCase();
    
    const querySnapshot = await getDocs(usersRef);
    const results = [];
    
    console.log('🔍 Buscando usuarios con término:', searchTerm);
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      
      const firstName = userData.firstName || '';
      const lastName = userData.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      // ✅ CORRECCIÓN: El email ES el ID del documento
      const email = doc.id;
      
      console.log(`📄 Usuario encontrado:`, {
        docId: doc.id,
        firstName,
        lastName,
        email
      });
      
      const firstNameLower = firstName.toLowerCase();
      const lastNameLower = lastName.toLowerCase();
      const fullNameLower = fullName.toLowerCase();
      const emailLower = email.toLowerCase();
      
      if (firstNameLower.includes(searchLower) || 
          lastNameLower.includes(searchLower) || 
          fullNameLower.includes(searchLower) || 
          emailLower.includes(searchLower)) {
        
        if (firstName) {
          const userResult = {
            uid: doc.id,
            firstName: firstName,
            lastName: lastName,
            displayName: fullName || email,
            email: email
          };
          
          console.log('✅ Usuario agregado a resultados:', userResult);
          results.push(userResult);
        }
      }
    });
    
    results.sort((a, b) => a.displayName.localeCompare(b.displayName));
    console.log(`✅ Total usuarios encontrados: ${results.length}`);
    setSearchResults(results.slice(0, 5));
  } catch (error) {
    console.error('❌ Error buscando usuarios:', error);
  } finally {
    setIsSearching(false);
  }
};

  // useEffect para la búsqueda con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (delegationMode === 'search') {
        searchUsers(delegatedUserSearch);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [delegatedUserSearch, delegationMode]);

  const handleSave = () => {
  let delegationData = {};
  
  if (canDelegateReservations) {
    if (delegationMode === 'none') {
      // ✅ CORRECCIÓN: Explícitamente eliminar la delegación
      delegationData = {
        delegatedTo: null
      };
    } else if (delegationMode === 'search' && delegatedUser) {
      delegationData = {
        delegatedTo: {
          uid: delegatedUser.uid,
          displayName: delegatedUser.displayName,
          email: delegatedUser.email,
          delegationType: 'database'
        }
      };
    } else if (delegationMode === 'manual' && manualDelegation.name.trim()) {
      delegationData = {
        delegatedTo: {
          displayName: manualDelegation.name.trim(),
          email: manualDelegation.email.trim(),
          delegationType: 'manual'
        }
      };
    }
  }
  
  onSaveEdit(delegationData);
};

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {isPeriodic ? 'Editar Reserva Periódica' : 'Editar Reserva'}
          </h3>
          <button onClick={onClose} className={styles.modalCloseButton}>
            <X size={20} />
          </button>
        </div>

        <form className={styles.editForm} onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Título
              <span className={styles.characterCount}>
                {editForm.title?.length || 0}/100
              </span>
            </label>
            <input
              type="text"
              name="title"
              value={editForm.title || ''}
              onChange={onEditFormChange}
              maxLength={100}
              className={styles.editInput}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Descripción
              <span className={styles.characterCount}>
                {editForm.description?.length || 0}/500
              </span>
            </label>
            <textarea
              name="description"
              value={editForm.description || ''}
              onChange={onEditFormChange}
              maxLength={500}
              className={styles.editTextarea}
            />
          </div>

          {!isPeriodic ? (
            <>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Fecha</label>
                <input
                  type="date"
                  name="date"
                  value={editForm.date || ''}
                  onChange={onEditFormChange}
                  className={styles.editInput}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Hora inicio</label>
                  <select
                    name="startTime"
                    value={editForm.startTime || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                  >
                    <option value="">Seleccionar</option>
                    {TIME_OPTIONS.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Hora fin</label>
                  <select
                    name="endTime"
                    value={editForm.endTime || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                    disabled={!editForm.startTime}
                  >
                    <option value="">Seleccionar</option>
                    {getEndTimeOptions(editForm.startTime).map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Fecha inicio</label>
                  <input
                    type="date"
                    name="startDate"
                    value={editForm.startDate || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Fecha fin</label>
                  <input
                    type="date"
                    name="endDate"
                    value={editForm.endDate || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Hora inicio</label>
                  <select
                    name="startTime"
                    value={editForm.startTime || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                  >
                    <option value="">Seleccionar</option>
                    {TIME_OPTIONS.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Hora fin</label>
                  <select
                    name="endTime"
                    value={editForm.endTime || ''}
                    onChange={onEditFormChange}
                    className={styles.editInput}
                    required
                    disabled={!editForm.startTime}
                  >
                    <option value="">Seleccionar</option>
                    {getEndTimeOptions(editForm.startTime).map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Días de la semana</label>
                <div className={styles.daysContainer}>
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => onDayToggle(day.value)}
                      className={`${styles.dayButton} ${
                        editForm.daysOfWeek?.includes(day.value) ? styles.dayButtonActive : ''
                      }`}
                    >
                      <span className={styles.dayShort}>{day.short}</span>
                      <span className={styles.dayFull}>{day.full}</span>
                    </button>
                  ))}
                </div>
              </div>

              {conflictData && conflictData.conflicts.length > 0 && (
                <div className={styles.editConflictWarning}>
                  <div className={styles.editConflictHeader}>
                    <AlertCircle size={18} />
                    <span className={styles.editConflictTitle}>
                      Conflictos Detectados
                    </span>
                  </div>
                  
                  <div className={styles.editConflictList}>
                    {conflictData.conflicts.map((conflict, idx) => (
                      <div key={idx} className={styles.editConflictItem}>
                        • {conflict.date} a las {conflict.startTime}-{conflict.endTime}
                      </div>
                    ))}
                  </div>

                  <div className={styles.editConflictResolutionContainer}>
                    <span className={styles.editConflictResolutionTitle}>
                      ¿Cómo deseas resolver los conflictos?
                    </span>
                    <div className={styles.editRadioGroup}>
                      <label className={styles.editRadioLabel}>
                        <input
                          type="radio"
                          name="conflictResolution"
                          value="omit"
                          checked={editForm.conflictResolution === 'omit'}
                          onChange={onResolutionChange}
                          className={styles.editRadioInput}
                        />
                        <div>
                          <div className={styles.editRadioTitle}>Omitir fechas con conflictos</div>
                          <div className={styles.editRadioDescription}>
                            No crear reservas en las fechas que tienen conflictos
                          </div>
                        </div>
                      </label>
                      
                      <label className={styles.editRadioLabel}>
                        <input
                          type="radio"
                          name="conflictResolution"
                          value="overwrite"
                          checked={editForm.conflictResolution === 'overwrite'}
                          onChange={onResolutionChange}
                          className={styles.editRadioInput}
                        />
                        <div>
                          <div className={styles.editRadioTitle}>Sobrescribir reservas existentes</div>
                          <div className={styles.editRadioDescription}>
                            Reemplazar las reservas conflictivas con las nuevas
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Sección de Delegación */}
          {canDelegateReservations && (
            <div className={styles.delegationSection}>
              <div className={styles.delegationHeader}>
                <label className={styles.formLabel}>
                  <User className={styles.inputIcon} />
                  Asignar Reserva
                </label>
                <p className={styles.delegationDescription}>
                  Asigna esta reserva a otro usuario
                </p>
              </div>

              <div className={styles.delegationModes}>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'none' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('none');
                    setDelegatedUser(null);
                    setDelegatedUserSearch('');
                    setManualDelegation({ name: '', email: '' });
                    setSearchResults([]);
                  }}
                >
                  Sin asignar
                </button>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'search' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('search');
                    setManualDelegation({ name: '', email: '' });
                  }}
                >
                  Buscar usuario
                </button>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'manual' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('manual');
                    setDelegatedUser(null);
                    setDelegatedUserSearch('');
                    setSearchResults([]);
                  }}
                >
                  Entrada manual
                </button>
              </div>

              {delegationMode === 'search' && (
                <div className={styles.userSearchContainer}>
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      value={delegatedUserSearch}
                      onChange={(e) => setDelegatedUserSearch(e.target.value)}
                      className={styles.editInput}
                      placeholder="Buscar por nombre..."
                    />
                  </div>

                  {isSearching && (
                    <div className={styles.searchingIndicator}>
                      Buscando usuarios...
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className={styles.searchResults}>
                      {searchResults.map((result) => (
                        <button
                          key={result.uid}
                          type="button"
                          className={`${styles.userResultItem} ${delegatedUser?.uid === result.uid ? styles.userResultSelected : ''}`}
                          onClick={() => {
                            setDelegatedUser(result);
                            setDelegatedUserSearch(result.displayName);
                            setSearchResults([]);
                          }}
                        >
                          <User size={16} className={styles.userResultIcon} />
                          <div className={styles.userResultInfo}>
                            <span className={styles.userResultName}>{result.displayName}</span>
                            <span className={styles.userResultEmail}>{result.email}</span>
                          </div>
                          {delegatedUser?.uid === result.uid && (
                            <Check size={16} className={styles.userResultCheck} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {delegatedUser && (
                    <div className={styles.selectedUserBadge}>
                      <User size={14} />
                      <span>Asignado a: {delegatedUser.displayName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setDelegatedUser(null);
                          setDelegatedUserSearch('');
                        }}
                        className={styles.removeUserBtn}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {delegationMode === 'manual' && (
                <div className={styles.manualDelegationContainer}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Nombre del usuario <span className={styles.requiredMarker}>*</span>
                    </label>
                    <input
                      type="text"
                      value={manualDelegation.name}
                      onChange={(e) => setManualDelegation(prev => ({ ...prev, name: e.target.value }))}
                      className={styles.editInput}
                      placeholder="Nombre completo"
                      maxLength={100}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Email (opcional)
                    </label>
                    <input
                      type="email"
                      value={manualDelegation.email}
                      onChange={(e) => setManualDelegation(prev => ({ ...prev, email: e.target.value }))}
                      className={styles.editInput}
                      placeholder="correo@ejemplo.com"
                    />
                    <p className={styles.emailNote}>
                      Si proporcionas el email, el usuario recibirá una notificación
                    </p>
                  </div>

                  {manualDelegation.name && (
                    <div className={styles.manualDelegationPreview}>
                      <User size={14} />
                      <span>
                        Se asignará a: {manualDelegation.name}
                        {manualDelegation.email && ` (${manualDelegation.email})`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.modalActions}>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              <X size={16} />
              <span>Cancelar</span>
            </button>
            <button type="submit" className={styles.saveButton}>
              <Save size={16} />
              <span>Guardar Cambios</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE: FILTROS
// ============================================
// ============================================
// COMPONENTE: FILTROS AVANZADOS
// ============================================
const FilterBar = ({ 
  activeFilter, 
  setActiveFilter, 
  counts,
  user,
  onFilterChange,
  unifiedReservations
}) => {
  const [advancedFilterType, setAdvancedFilterType] = useState('mine');
  const [authorSearch, setAuthorSearch] = useState('');
  const [delegateSearch, setDelegateSearch] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [authorResults, setAuthorResults] = useState([]);
  const [delegateResults, setDelegateResults] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  const [isSearchingAuthors, setIsSearchingAuthors] = useState(false);
  const [isSearchingDelegates, setIsSearchingDelegates] = useState(false);

  // ⬇️ AGREGAR ESTAS DOS FUNCIONES AQUÍ ⬇️
  
  // Manejar tecla Enter en búsqueda de autores
  const handleAuthorKeyDown = (e) => {
    if (e.key === 'Enter' && authorResults.length > 0) {
      e.preventDefault();
      const firstResult = authorResults[0];
      setSelectedAuthor(firstResult);
      setAuthorSearch(firstResult.displayName);
      setAuthorResults([]);
    }
  };

  // Manejar tecla Enter en búsqueda de delegados
  const handleDelegateKeyDown = (e) => {
    if (e.key === 'Enter' && delegateResults.length > 0) {
      e.preventDefault();
      const firstResult = delegateResults[0];
      setSelectedDelegate(firstResult);
      setDelegateSearch(firstResult.displayName);
      setDelegateResults([]);
    }
  };

  // ⬆️ FIN DE LAS FUNCIONES NUEVAS ⬆️

  // Búsqueda de autores (DESDE RESERVAS, NO DESDE USERS)
  const searchAuthors = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setAuthorResults([]);
      return;
    }

    setIsSearchingAuthors(true);
    try {
      const searchLower = searchTerm.toLowerCase();
      
      // Crear un Map para evitar duplicados
      const authorsMap = new Map();
      
      // Buscar en las reservas unificadas disponibles
      unifiedReservations.forEach(res => {
        if (res.reservedBy) {
          const displayName = res.reservedBy.displayName || '';
          const email = res.reservedBy.email || '';
          const uid = res.reservedBy.uid || '';
          
          const displayNameLower = displayName.toLowerCase();
          
          // Buscar SOLO por nombre, NO por email
          if (displayNameLower.includes(searchLower)) {
            if (!authorsMap.has(uid)) {
              authorsMap.set(uid, {
                uid: uid,
                displayName: displayName,
                email: email
              });
            }
          }
        }
      });
      
      const results = Array.from(authorsMap.values());
      results.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setAuthorResults(results.slice(0, 5));
    } catch (error) {
      console.error('Error buscando autores:', error);
    } finally {
      setIsSearchingAuthors(false);
    }
  }, [unifiedReservations]);

  // Búsqueda de delegados (DESDE RESERVAS, NO DESDE USERS)
  const searchDelegates = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setDelegateResults([]);
      return;
    }

    setIsSearchingDelegates(true);
    try {
      const searchLower = searchTerm.toLowerCase();
      
      // Crear un Map para evitar duplicados
      const delegatesMap = new Map();
      
      // Buscar en las reservas unificadas disponibles
      unifiedReservations.forEach(res => {
        if (res.delegatedTo) {
          const displayName = res.delegatedTo.displayName || '';
          const email = res.delegatedTo.email || '';
          const uid = res.delegatedTo.uid || displayName; // usar displayName como key si no hay uid
          
          const displayNameLower = displayName.toLowerCase();
          
          // Buscar SOLO por nombre, NO por email
          if (displayNameLower.includes(searchLower)) {
            if (!delegatesMap.has(uid)) {
              delegatesMap.set(uid, {
                uid: uid,
                displayName: displayName,
                email: email,
                delegationType: res.delegatedTo.delegationType
              });
            }
          }
        }
      });
      
      const results = Array.from(delegatesMap.values());
      results.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setDelegateResults(results.slice(0, 5));
    } catch (error) {
      console.error('Error buscando delegados:', error);
    } finally {
      setIsSearchingDelegates(false);
    }
  }, [unifiedReservations]);

  // ... resto del código del componente FilterBar
  // Debounce para búsqueda de autores
  useEffect(() => {
    const timer = setTimeout(() => {
      if (advancedFilterType === 'author') {
        searchAuthors(authorSearch);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [authorSearch, advancedFilterType]);

  // Debounce para búsqueda de delegados
  useEffect(() => {
    const timer = setTimeout(() => {
      if (advancedFilterType === 'delegate') {
        searchDelegates(delegateSearch);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [delegateSearch, advancedFilterType]);

  const handleFilterTypeChange = (type) => {
    setAdvancedFilterType(type);
    // Limpiar estados según el tipo
    if (type !== 'author') {
      setAuthorSearch('');
      setSelectedAuthor(null);
      setAuthorResults([]);
    }
    if (type !== 'delegate') {
      setDelegateSearch('');
      setSelectedDelegate(null);
      setDelegateResults([]);
    }
    if (type !== 'dateRange') {
      setDateRange({ start: '', end: '' });
    }
  };
  // Comunicar cambios de filtro al componente padre
  useEffect(() => {
    const filterData = {
      type: advancedFilterType,
      author: selectedAuthor,
      delegate: selectedDelegate,
      dateRange
    };
    onFilterChange(filterData);
  }, [advancedFilterType, selectedAuthor, selectedDelegate, dateRange, onFilterChange]);

  
  return (
    <div className={styles.filterContainer}>
      <div className={styles.filterHeader}>
        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            <Filter size={16} />
            <span className={styles.filterButtonText}>Todas</span>
            <span className={styles.filterBadge}>{counts.all}</span>
          </button>
          
          <button
            className={`${styles.filterButton} ${activeFilter === 'upcoming' ? styles.active : ''}`}
            onClick={() => setActiveFilter('upcoming')}
          >
            <CalendarClock size={16} />
            <span className={styles.filterButtonText}>Próximas</span>
            <span className={styles.filterBadge}>{counts.upcoming}</span>
          </button>
          
          <button
            className={`${styles.filterButton} ${activeFilter === 'past' ? styles.active : ''}`}
            onClick={() => setActiveFilter('past')}
          >
            <Calendar size={16} />
            <span className={styles.filterButtonText}>Pasadas</span>
            <span className={styles.filterBadge}>{counts.past}</span>
          </button>
        </div>
        
        <span className={styles.resultsCount}>
          {activeFilter === 'all' ? counts.all : activeFilter === 'upcoming' ? counts.upcoming : counts.past} resultados
        </span>
      </div>

      {/* Filtros Avanzados */}
      <div className={styles.advancedFiltersContainer}>
        <div className={styles.filterTypeSelector}>
          <button
            className={`${styles.filterTypeBtn} ${advancedFilterType === 'mine' ? styles.filterTypeActive : ''}`}
            onClick={() => handleFilterTypeChange('mine')}
          >
            <User size={14} />
            Mis reservas
          </button>
          <button
            className={`${styles.filterTypeBtn} ${advancedFilterType === 'author' ? styles.filterTypeActive : ''}`}
            onClick={() => handleFilterTypeChange('author')}
          >
            <Users size={14} />
            Por autor
          </button>
          <button
            className={`${styles.filterTypeBtn} ${advancedFilterType === 'delegate' ? styles.filterTypeActive : ''}`}
            onClick={() => handleFilterTypeChange('delegate')}
          >
            <UserCheck size={14} />
            Por asignado
          </button>
          <button
            className={`${styles.filterTypeBtn} ${advancedFilterType === 'dateRange' ? styles.filterTypeActive : ''}`}
            onClick={() => handleFilterTypeChange('dateRange')}
          >
            <Calendar size={14} />
            Por fechas
          </button>
        </div>

        {/* Panel de búsqueda por autor */}
        {advancedFilterType === 'author' && (
          <div className={styles.filterPanel}>
            <div className={styles.searchInputContainer}>
              <input
                type="text"
                value={authorSearch}
                onChange={(e) => setAuthorSearch(e.target.value)}
                onKeyDown={handleAuthorKeyDown}
                placeholder="Buscar autor por nombre..."
                className={styles.searchInput}
              />
              {isSearchingAuthors && (
                <div className={styles.searchSpinner}>
                  <div className={styles.spinner}></div>
                </div>
              )}
            </div>

            {authorResults.length > 0 && (
              <div className={styles.searchResultsDropdown}>
                {authorResults.map((result) => (
                  <button
                    key={result.uid}
                    type="button"
                    className={`${styles.resultItem} ${selectedAuthor?.uid === result.uid ? styles.resultItemSelected : ''}`}
                    onClick={() => {
                      setSelectedAuthor(result);
                      setAuthorSearch(result.displayName);
                      setAuthorResults([]);
                    }}
                  >
                    <User size={14} className={styles.resultIcon} />
                    <div className={styles.resultInfo}>
                      <span className={styles.resultName}>{result.displayName}</span>
                      <span className={styles.resultEmail}>{result.email}</span>
                    </div>
                    {selectedAuthor?.uid === result.uid && (
                      <Check size={14} className={styles.resultCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {selectedAuthor && (
              <div className={styles.selectedBadge}>
                <User size={12} />
                <span>Filtrando por: {selectedAuthor.displayName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAuthor(null);
                    setAuthorSearch('');
                  }}
                  className={styles.clearBtn}
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Panel de búsqueda por delegado */}
        {advancedFilterType === 'delegate' && (
          <div className={styles.filterPanel}>
            <div className={styles.searchInputContainer}>
              <input
                type="text"
                value={delegateSearch}
                onChange={(e) => setDelegateSearch(e.target.value)}
                onKeyDown={handleDelegateKeyDown}
                placeholder="Buscar asignado por nombre..."
                className={styles.searchInput}
              />
              {isSearchingDelegates && (
                <div className={styles.searchSpinner}>
                  <div className={styles.spinner}></div>
                </div>
              )}
            </div>

            {delegateResults.length > 0 && (
              <div className={styles.searchResultsDropdown}>
                {delegateResults.map((result) => (
                  <button
                    key={result.uid}
                    type="button"
                    className={`${styles.resultItem} ${selectedDelegate?.uid === result.uid ? styles.resultItemSelected : ''}`}
                    onClick={() => {
                      setSelectedDelegate(result);
                      setDelegateSearch(result.displayName);
                      setDelegateResults([]);
                    }}
                  >
                    <UserCheck size={14} className={styles.resultIcon} />
                    <div className={styles.resultInfo}>
                      <span className={styles.resultName}>{result.displayName}</span>
                      <span className={styles.resultEmail}>{result.email}</span>
                    </div>
                    {selectedDelegate?.uid === result.uid && (
                      <Check size={14} className={styles.resultCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {selectedDelegate && (
              <div className={styles.selectedBadge}>
                <UserCheck size={12} />
                <span>Filtrando por: {selectedDelegate.displayName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDelegate(null);
                    setDelegateSearch('');
                  }}
                  className={styles.clearBtn}
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Panel de rango de fechas */}
        {advancedFilterType === 'dateRange' && (
          <div className={styles.filterPanel}>
            <div className={styles.dateRangeContainer}>
              <div className={styles.dateInputGroup}>
                <label className={styles.dateLabel}>Desde:</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className={styles.dateInput}
                />
              </div>
              <div className={styles.dateInputGroup}>
                <label className={styles.dateLabel}>Hasta:</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className={styles.dateInput}
                  min={dateRange.start}
                />
              </div>
            </div>

            {(dateRange.start || dateRange.end) && (
              <div className={styles.selectedBadge}>
                <Calendar size={12} />
                <span>
                  {dateRange.start && dateRange.end 
                    ? `${new Date(dateRange.start).toLocaleDateString('es-ES')} - ${new Date(dateRange.end).toLocaleDateString('es-ES')}`
                    : dateRange.start 
                      ? `Desde ${new Date(dateRange.start).toLocaleDateString('es-ES')}`
                      : `Hasta ${new Date(dateRange.end).toLocaleDateString('es-ES')}`
                  }
                </span>
                <button
                  type="button"
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className={styles.clearBtn}
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
        )}

        {advancedFilterType === 'mine' && (
          <div className={styles.filterPanel}>
            <div className={styles.mineFilterInfo}>
              <User size={16} className={styles.mineIcon} />
              <span>Mostrando solo tus reservas personales</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE: TARJETA DE RESERVA COMPACTA
// ============================================
// ============================================
// COMPONENTE: TARJETA DE RESERVA MEJORADA
// ============================================
const ReservationCard = ({ 
  reservation, 
  onEdit, 
  onDelete,
  onCardClick,
  isPast 
}) => {
  const now = new Date();
  const reservationDateTime = reservation.type === 'periodic' 
    ? new Date(`${reservation.startDate}T${reservation.startTime}`)
    : new Date(`${reservation.date}T${reservation.startTime}`);
  const isReservationPast = reservationDateTime < now;
  const isPeriodic = reservation.type === 'periodic';

  const getDayNames = (daysArray) => {
    const dayNames = { 1: 'L', 2: 'M', 3: 'X', 4: 'J', 5: 'V' };
    return daysArray.map(d => dayNames[d]).join(', ');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleCardClick = (e) => {
    // Evitar abrir detalles si se hace clic en los botones de acción
    if (e.target.closest(`.${styles.cardActions}`)) {
      return;
    }
    onCardClick(reservation);
  };

  return (
    <div className={`${styles.reservationCard} ${isReservationPast ? styles.pastReservation : ''}`}>
      <div className={styles.cardMain}>
        <div className={styles.cardLeft} onClick={handleCardClick}>
          <div className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>{reservation.title}</h4>
          </div>

          <div className={styles.cardInfo}>
            {isPeriodic ? (
              <>
                <div className={styles.infoRow}>
                  <Calendar className={styles.infoIcon} size={14} />
                  <span>{formatDate(reservation.startDate)} - {formatDate(reservation.endDate)}</span>
                </div>
                <div className={styles.infoRow}>
                  <Clock className={styles.infoIcon} size={14} />
                  <span>{reservation.startTime} - {reservation.endTime}</span>
                </div>
                <div className={styles.infoRow}>
                  <Repeat className={styles.infoIcon} size={14} />
                  <span className={styles.periodicTag}>
                    {getDayNames(reservation.daysOfWeek)} • {reservation.occurrencesCount} sesiones
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.infoRow}>
                  <Calendar className={styles.infoIcon} size={14} />
                  <span>{formatDate(reservation.date)}</span>
                </div>
                <div className={styles.infoRow}>
                  <Clock className={styles.infoIcon} size={14} />
                  <span>{reservation.startTime} - {reservation.endTime}</span>
                </div>
              </>
            )}
            
            {/* NUEVA SECCIÓN: Mostrar autor y delegado en row horizontal */}
            <div className={styles.userInfoRow}>
              {/* Reservado por (autor) */}
              <div className={styles.userInfoItem}>
                <User className={styles.userIcon} size={12} />
                <span className={styles.userLabel}>Reservado por:</span>
                <span className={styles.authorName}>
                  {reservation.reservedBy?.displayName || reservation.userName || 'Usuario'}
                </span>
              </div>
              
              {/* Asignado a (solo si existe delegación) */}
              {reservation.delegatedTo && (
                <>
                  <span className={styles.userSeparator}>•</span>
                  <div className={styles.userInfoItem}>
                    <UserCheck className={styles.userIcon} size={12} />
                    <span className={styles.userLabel}>Asignado a:</span>
                    <span className={styles.delegatedName}>
                      {reservation.delegatedTo.displayName}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.cardRight}>
          {isReservationPast ? (
            <span className={styles.pastBadge}>
              <CheckCircle size={12} />
              Pasada
            </span>
          ) : (
            <span className={styles.upcomingBadge}>
              <AlertCircle size={12} />
              Próxima
            </span>
          )}
          
          <div className={styles.cardActions}>
            <button
              onClick={() => onEdit(reservation)}
              className={styles.cardEditBtn}
              title="Editar"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => onDelete(reservation)}
              className={styles.cardDeleteBtn}
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE: LISTA DE PRÓXIMAS RESERVAS
// ============================================
const UpcomingReservationsList = ({ reservations, onEdit, onDelete, onCardClick }) => {
  if (reservations.length === 0) {
    return (
      <div className={styles.emptyState}>
        <CalendarClock className={styles.emptyIcon} />
        <p className={styles.emptyMessage}>No hay reservas próximas</p>
        <p className={styles.emptySubtext}>Tus próximas reservas aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className={styles.reservationsList}>
      {reservations.map((reservation) => (
        <ReservationCard
          key={reservation.id}
          reservation={reservation}
          onEdit={onEdit}
          onDelete={onDelete}
          onCardClick={onCardClick}
          isPast={false}
        />
      ))}
    </div>
  );
};

// ============================================
// COMPONENTE: RESERVAS PASADAS POR MES
// ============================================
const PastReservationsByMonth = ({ 
  groupedReservations, 
  sortedMonthKeys, 
  expandedMonths, 
  toggleMonth,
  onEdit,
  onDelete,
  onCardClick
}) => {
  if (sortedMonthKeys.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Calendar className={styles.emptyIcon} />
        <p className={styles.emptyMessage}>No hay reservas pasadas</p>
        <p className={styles.emptySubtext}>Tus reservas pasadas aparecerán aquí</p>
      </div>
    );}

  return (
    <div className={styles.monthsContainer}>
      {sortedMonthKeys.map((monthKey) => (
        <div key={monthKey} className={styles.monthBlock}>
          <div 
            className={styles.monthHeader}
            onClick={() => toggleMonth(monthKey)}
          >
            <h3 className={styles.monthTitle}>{monthKey}</h3>
            <div className={styles.monthInfo}>
              <span className={styles.monthCount}>
                {groupedReservations[monthKey].length}
              </span>
              {expandedMonths[monthKey] ? 
                <ChevronUp className={styles.toggleIcon} /> : 
                <ChevronDown className={styles.toggleIcon} />
              }
            </div>
          </div>
          
          {expandedMonths[monthKey] && (
            <div className={styles.monthReservationsList}>
              {groupedReservations[monthKey].map((reservation) => (
                <ReservationCard
                  key={reservation.id}
                  reservation={reservation}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCardClick={onCardClick}
                  isPast={true}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// COMPONENTE: PAGINACIÓN
// ============================================
const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  itemsPerPage, 
  onItemsPerPageChange,
  totalItems 
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.paginationWrapper}>
        <div className={styles.paginationInfo}>
          {startItem}-{endItem} de {totalItems}
        </div>

        <div className={styles.paginationControls}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            <ChevronLeft size={16} />
          </button>

          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className={styles.pageEllipsis}>...</span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`${styles.pageButton} ${currentPage === page ? styles.active : ''}`}
              >
                {page}
              </button>
            )
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className={styles.pageSizeSelector}>
          <span>Mostrar:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className={styles.pageSizeSelect}
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const MyReservations = ({ user, space, onClose, canDelegateReservations = false }) => {
  // 🆕 URL DEL BACKEND EN RENDER
  const API_BASE_URL = 'https://truebavirtual.onrender.com';

  const [reservations, setReservations] = useState([]);
  const [periodicReservations, setPeriodicReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('upcoming');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});

  const [conflictData, setConflictData] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
   const [advancedFilters, setAdvancedFilters] = useState({
    type: 'mine',
    author: null,
    delegate: null,
    dateRange: { start: '', end: '' }
  });

  const handleFilterChange = useCallback((filterData) => {
    setAdvancedFilters(filterData);
    setCurrentPage(1); // Reset página al cambiar filtros
  }, []);

  // Cargar reservas manuales y periódicas
  // Cargar reservas manuales y periódicas
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const reservationsRef = collection(db, 'reservations');
    const unsubscribeReservations = onSnapshot(
      reservationsRef,
      (snapshot) => {
        const allReservations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // ✅ SOLO filtrar por espacio, NO por usuario
        // El filtro por usuario se hará en el filteredReservations
        const spaceReservations = space 
          ? allReservations.filter(res => res.space === space.name)
          : allReservations;
        
        setReservations(spaceReservations);
        setLoading(false);
      },
      (err) => {
        setError('Error al cargar las reservas');
        setLoading(false);
        console.error('Error:', err);
      }
    );

    const periodicRef = collection(db, 'periodicReservations');
    const unsubscribePeriodic = onSnapshot(
      periodicRef,
      (snapshot) => {
        const allPeriodic = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // ✅ SOLO filtrar por espacio, NO por usuario
        const spacePeriodic = space
          ? allPeriodic.filter(res => res.space === space.name)
          : allPeriodic;
        
        setPeriodicReservations(spacePeriodic);
      },
      (err) => console.error('Error al cargar periódicas:', err)
    );

    return () => {
      unsubscribeReservations();
      unsubscribePeriodic();
    };
  }, [user, space]);

  // Unificar reservas manuales y periódicas
  const unifiedReservations = useMemo(() => {
    const periodicGroups = {};
    const manualReservations = [];

    reservations.forEach(res => {
      if (res.type === 'periodic' && res.periodicId) {
        if (!periodicGroups[res.periodicId]) periodicGroups[res.periodicId] = [];
        periodicGroups[res.periodicId].push(res);
      } else {
        manualReservations.push(res);
      }
    });

    const unified = [];

    Object.keys(periodicGroups).forEach(periodicId => {
      const periodicMaster = periodicReservations.find(p => p.id === periodicId);
      if (periodicMaster) {
        unified.push({
          id: periodicId,
          type: 'periodic',
          isPeriodic: true,
          ...periodicMaster,
          instances: periodicGroups[periodicId]
        });
      }
    });

    manualReservations.forEach(res => {
      unified.push({ ...res, isPeriodic: false });
    });

    return unified;
  }, [reservations, periodicReservations]);

  // Obtener autores y delegados únicos
  // Obtener autores y delegados únicos (de TODAS las reservas, no solo del usuario)
  const { availableAuthors, availableDelegates } = useMemo(() => {
    const authorsMap = new Map();
    const delegatesMap = new Map();

    unifiedReservations.forEach(res => {
      if (res.reservedBy) {
        authorsMap.set(res.reservedBy.uid, res.reservedBy);
      }
      if (res.delegatedTo) {
        const key = res.delegatedTo.uid || res.delegatedTo.displayName;
        delegatesMap.set(key, res.delegatedTo);
      }
    });

    return {
      availableAuthors: Array.from(authorsMap.values()),
      availableDelegates: Array.from(delegatesMap.values())
    };
  }, [unifiedReservations]);

  // Filtrar reservas
 // Filtrar reservas
  // Filtrar reservas con lógica mejorada
  // Filtrar reservas con lógica mejorada y completa
  const filteredReservations = useMemo(() => {
    return unifiedReservations.filter(res => {
      // ============================================
      // FILTRO SEGÚN EL TIPO SELECCIONADO
      // ============================================
      
      if (advancedFilters.type === 'mine') {
        // ✅ MODO "MIS RESERVAS": Solo las del usuario actual
        const isUserReservation = res.reservedBy?.uid === user.uid ||
          (res.delegatedTo?.uid === user.uid) ||
          (res.delegatedTo && !res.delegatedTo.uid);
        
        if (!isUserReservation) return false;
      } 
      else if (advancedFilters.type === 'author') {
  // ✅ MODO "POR AUTOR": Filtrar por autor específico si hay uno seleccionado
  if (advancedFilters.author) {
    // Si no hay reservedBy, excluir
    if (!res.reservedBy) {
      return false;
    }
    
    // Comparar por displayName, email o uid
    const matchByDisplayName = 
      res.reservedBy.displayName && 
      advancedFilters.author.displayName &&
      res.reservedBy.displayName.toLowerCase() === advancedFilters.author.displayName.toLowerCase();
    
    const matchByEmail = 
      res.reservedBy.email && 
      advancedFilters.author.email &&
      res.reservedBy.email.toLowerCase() === advancedFilters.author.email.toLowerCase();
    
    const matchByUid = 
      res.reservedBy.uid && 
      advancedFilters.author.uid &&
      res.reservedBy.uid === advancedFilters.author.uid;
    
    // Debe coincidir por al menos uno de los criterios
    if (!matchByDisplayName && !matchByEmail && !matchByUid) {
      return false;
    }
  } else {
    // Si no hay autor seleccionado, mostrar todas las reservas del espacio
    return true;
  }
}
      else if (advancedFilters.type === 'delegate') {
        // ✅ MODO "POR DELEGADO": Filtrar por delegado específico
        if (advancedFilters.delegate) {
          const delegateMatch = 
            res.delegatedTo?.uid === advancedFilters.delegate.uid ||
            res.delegatedTo?.displayName === advancedFilters.delegate.displayName;
          
          if (!delegateMatch) return false;
        } else {
          // Si no hay delegado seleccionado, mostrar solo las que tienen delegación
          if (!res.delegatedTo) return false;
        }
      }
      else if (advancedFilters.type === 'dateRange') {
        // ✅ MODO "POR FECHAS": Filtrar por rango de fechas
        const startFilter = advancedFilters.dateRange.start;
        const endFilter = advancedFilters.dateRange.end;
        
        // Si no hay fechas seleccionadas, mostrar todo
        if (!startFilter && !endFilter) {
          return true;
        }
        
        if (res.isPeriodic) {
          // Para reservas periódicas: verificar si hay solapamiento con el rango
          const periodicStart = res.startDate;
          const periodicEnd = res.endDate;
          
          // Si tiene filtro de inicio y la reserva termina antes, excluir
          if (startFilter && periodicEnd < startFilter) {
            return false;
          }
          
          // Si tiene filtro de fin y la reserva empieza después, excluir
          if (endFilter && periodicStart > endFilter) {
            return false;
          }
          
          // Si llegamos aquí, hay solapamiento
        } else {
          // Para reservas manuales: verificar si la fecha está dentro del rango
          const resDate = res.date;
          
          // Si tiene filtro de inicio y la fecha es anterior, excluir
          if (startFilter && resDate < startFilter) {
            return false;
          }
          
          // Si tiene filtro de fin y la fecha es posterior, excluir
          if (endFilter && resDate > endFilter) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [unifiedReservations, user, advancedFilters]);

  // Contar reservas por filtro
  const counts = useMemo(() => {
    const now = new Date();
    const upcoming = filteredReservations.filter(res => {
      const resDate = res.isPeriodic 
        ? new Date(`${res.startDate}T${res.startTime}`)
        : new Date(`${res.date}T${res.startTime}`);
      return resDate >= now;
    }).length;
    
    const past = filteredReservations.filter(res => {
      const resDate = res.isPeriodic 
        ? new Date(`${res.startDate}T${res.startTime}`)
        : new Date(`${res.date}T${res.startTime}`);
      return resDate < now;
    }).length;
    
    return {
      all: filteredReservations.length,
      upcoming,
      past
    };
  }, [filteredReservations]);

  // Ordenar y paginar reservas próximas
  const upcomingReservations = useMemo(() => {
    const now = new Date();
    const upcoming = filteredReservations
      .filter(res => {
        const resDate = res.isPeriodic 
          ? new Date(`${res.startDate}T${res.startTime}`)
          : new Date(`${res.date}T${res.startTime}`);
        return resDate >= now;
      })
      .sort((a, b) => {
        const dateA = a.isPeriodic 
          ? new Date(`${a.startDate}T${a.startTime}`)
          : new Date(`${a.date}T${a.startTime}`);
        const dateB = b.isPeriodic 
          ? new Date(`${b.startDate}T${b.startTime}`)
          : new Date(`${b.date}T${b.startTime}`);
        return dateA - dateB;
      });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return upcoming.slice(startIndex, endIndex);
  }, [filteredReservations, currentPage, itemsPerPage]);

  // Agrupar reservas pasadas por mes
  const { groupedPastReservations, sortedMonthKeys } = useMemo(() => {
    const now = new Date();
    const past = filteredReservations.filter(res => {
      const resDate = res.isPeriodic 
        ? new Date(`${res.startDate}T${res.startTime}`)
        : new Date(`${res.date}T${res.startTime}`);
      return resDate < now;
    });

    const grouped = {};
    past.forEach(res => {
      const date = res.isPeriodic ? new Date(res.startDate) : new Date(res.date);
      const monthName = date.toLocaleString('es-ES', { month: 'long' });
      const year = date.getFullYear();
      const key = `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
      
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(res);
    });

    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const dateA = a.isPeriodic 
          ? new Date(`${a.startDate}T${a.startTime}`)
          : new Date(`${a.date}T${a.startTime}`);
        const dateB = b.isPeriodic 
          ? new Date(`${b.startDate}T${b.startTime}`)
          : new Date(`${b.date}T${b.startTime}`);
        return dateB - dateA;
      });
    });

    const sorted = Object.keys(grouped).sort((a, b) => {
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
      return MONTH_MAP[monthB] - MONTH_MAP[monthA];
    });

    return { groupedPastReservations: grouped, sortedMonthKeys: sorted };
  }, [filteredReservations]);

  // Calcular páginas totales
  const totalPages = Math.ceil(counts.upcoming / itemsPerPage);

  // Handlers
  const handleEdit = useCallback(async (reservation) => {
    setEditingReservation(reservation);
    
    if (reservation.isPeriodic) {
      setEditForm({
        title: reservation.title,
        description: reservation.description,
        startDate: reservation.startDate,
        endDate: reservation.endDate,
        startTime: reservation.startTime,
        endTime: reservation.endTime,
        daysOfWeek: reservation.daysOfWeek || [],
        conflictResolution: 'omit'
      });
      
      await checkConflicts(reservation);
    } else {
      setEditForm({
        title: reservation.title,
        description: reservation.description,
        date: reservation.date,
        startTime: reservation.startTime,
        endTime: reservation.endTime
      });
    }
  }, []);

  const checkConflicts = async (reservation) => {
    if (!reservation.isPeriodic || !editForm.startDate || !editForm.endDate || !editForm.daysOfWeek?.length) {
      setConflictData(null);
      return;
    }

    try {
      const allReservationsSnapshot = await getDocs(collection(db, 'reservations'));
      const allReservations = allReservationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const conflicts = [];
      const start = new Date(editForm.startDate);
      const end = new Date(editForm.endDate);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
        
        if (editForm.daysOfWeek.includes(adjustedDay)) {
          const dateStr = d.toISOString().split('T')[0];
          
          const conflict = allReservations.find(r => 
            r.id !== reservation.id &&
            r.space === reservation.space &&
            r.date === dateStr &&
            ((editForm.startTime >= r.startTime && editForm.startTime < r.endTime) ||
             (editForm.endTime > r.startTime && editForm.endTime <= r.endTime) ||
             (editForm.startTime <= r.startTime && editForm.endTime >= r.endTime))
          );
          
          if (conflict) {
            conflicts.push({
              date: dateStr,
              startTime: conflict.startTime,
              endTime: conflict.endTime,
              conflictId: conflict.id
            });
          }
        }
      }

      setConflictData(conflicts.length > 0 ? { conflicts } : null);
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  };

  const handleDelete = useCallback(async (reservation) => {
    const confirmMessage = reservation.isPeriodic
      ? `¿Eliminar toda la serie periódica?\n\nEsto eliminará ${reservation.occurrencesCount} reservas.`
      : '¿Estás seguro de que quieres eliminar esta reserva?';

    if (window.confirm(confirmMessage)) {
      try {
        if (reservation.isPeriodic) {
          const batch = writeBatch(db);
          const instancesQuery = query(
            collection(db, 'reservations'),
            where('periodicId', '==', reservation.id)
          );
          const instancesSnapshot = await getDocs(instancesQuery);
          
          instancesSnapshot.forEach(docSnap => {
            batch.delete(doc(db, 'reservations', docSnap.id));
          });
          
          batch.delete(doc(db, 'periodicReservations', reservation.id));
          await batch.commit();
        } else {
          await deleteDoc(doc(db, 'reservations', reservation.id));
        }
      } catch (err) {
        alert('Error al eliminar la reserva');
        console.error(err);
      }
    }
  }, []);

  // ============================================
  // 🆕 FUNCIÓN PARA ENVIAR EMAILS AL EDITAR RESERVA
  // ============================================
  const sendEditNotificationEmails = async (reservationData, oldDelegatedTo, newDelegatedTo) => {
  const API_BASE_URL = 'https://truebavirtual.onrender.com';

  try {
    const creatorName = user.displayName || 'Usuario';
    
    console.log('📧 === INICIANDO ENVÍO DE EMAILS DE EDICIÓN ===');
    let assignedName = null;
    let assignedEmail = null;
    
    if (newDelegatedTo) {
      assignedName = newDelegatedTo.displayName;
      assignedEmail = newDelegatedTo.email;
      console.log('✅ Nuevo usuario asignado:', { assignedName, assignedEmail });
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return 'No especificada';
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      });
    };

    // 🎯 SOLO ENVIAR AL ASIGNADO (IGUAL QUE CreateReservationModal)
    if (assignedEmail && assignedEmail !== user.email) {
      console.log('📤 Enviando email al nuevo usuario asignado...');
      
      const assignedEmailData = {
        assignedEmail: assignedEmail,
        assignedName: assignedName,
        creatorName: creatorName,
        reservationId: reservationData.id,
        spaceName: reservationData.space || 'Espacio',
        title: reservationData.title || 'Sin título',
        description: reservationData.description || '',
        date: reservationData.date ? formatDate(reservationData.date) : null,
        startDate: reservationData.startDate ? formatDate(reservationData.startDate) : null,
        endDate: reservationData.endDate ? formatDate(reservationData.endDate) : null,
        timeStart: reservationData.startTime || 'No especificado',
        timeEnd: reservationData.endTime || 'No especificado',
        isPeriodic: reservationData.type === 'periodic'
      };

      console.log('📋 Datos del email:', assignedEmailData);
      
      try {
        const assignedResponse = await fetch(
          `${API_BASE_URL}/api/reservations/notify-assigned`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assignedEmailData)
          }
        );

        const assignedResult = await assignedResponse.json();
        
        if (!assignedResponse.ok) {
          console.error('❌ Error:', assignedResult);
        } else {
          console.log('✅ Email enviado al asignado exitosamente:', assignedResult.message);
        }
      } catch (error) {
        console.error('❌ Error al enviar email:', error);
      }
    } else {
      if (!assignedEmail) {
        console.log('ℹ️ No se enviará email (sin email proporcionado)');
      } else if (assignedEmail === user.email) {
        console.log('ℹ️ El asignado es el mismo creador, no se envía duplicado');
      }
    }
    
    console.log('📧 === ENVÍO COMPLETADO ===');
    
  } catch (error) {
    console.error('❌ Error general:', error);
  }
};

  const handleSaveEdit = useCallback(async (delegationData) => {
  try {
    if (!editingReservation) return;

    const updatedData = {
      ...editForm,
      ...delegationData,
      updatedAt: new Date()
    };

    await updateDoc(doc(db, 'reservations', editingReservation.id), updatedData);

    // ✅ LLAMAR AL ENVÍO DE CORREOS
    if (delegationData.delegatedTo) {
      const newDelegatedTo = delegationData.delegatedTo;
      const oldDelegatedTo = editingReservation.delegatedTo;

      if (newDelegatedTo && (!oldDelegatedTo || oldDelegatedTo.email !== newDelegatedTo.email)) {
        await sendEditNotificationEmails(
          {
            id: editingReservation.id,
            type: editingReservation.type,
            space: editingReservation.space,
            title: editForm.title,
            description: editForm.description,
            date: editForm.date || editForm.startDate,
            startTime: editForm.startTime,
            endTime: editForm.endTime,
            reservedBy: editingReservation.reservedBy
          },
          oldDelegatedTo,
          newDelegatedTo
        );
      }
    }
    
    setEditingReservation(null);
    setEditForm({});
    setConflictData(null);
  } catch (err) {
    alert('Error al guardar los cambios');
    console.error(err);
  }
}, [editingReservation, editForm, conflictData, user.uid, user.displayName, user.email]);

  const handleEditFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      const updated = { ...prev, [name]: value };
      
      if (editingReservation?.isPeriodic && 
          (name === 'startDate' || name === 'endDate' || name === 'startTime' || name === 'endTime')) {
        setTimeout(() => checkConflicts(editingReservation), 100);
      }
      
      return updated;
    });
  }, [editingReservation]);

  const handleDayToggle = useCallback((day) => {
    setEditForm(prev => {
      const daysOfWeek = prev.daysOfWeek || [];
      const updated = {
        ...prev,
        daysOfWeek: daysOfWeek.includes(day)
          ? daysOfWeek.filter(d => d !== day)
          : [...daysOfWeek, day].sort((a, b) => a - b)
      };
      
      setTimeout(() => checkConflicts(editingReservation), 100);
      return updated;
    });
  }, [editingReservation]);

  const handleResolutionChange = useCallback((e) => {
    setEditForm(prev => ({
      ...prev,
      conflictResolution: e.target.value
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const toggleMonth = useCallback((monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  }, []);

  const handleCardClick = useCallback((reservation) => {
    setSelectedReservation(reservation);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedReservation(null);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <AlertCircle className={styles.errorIcon} />
          <span>Error: Usuario no autenticado</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Cargando reservas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <AlertCircle className={styles.errorIcon} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>Mis Reservas</h2>
        <button onClick={onClose} className={styles.closeButton}>
          <X size={20} />
        </button>
      </header>

      <FilterBar
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        counts={counts}
        user={user}
        onFilterChange={handleFilterChange}
        unifiedReservations={unifiedReservations} // ⬅️ NUEVA PROP
      />

      <div className={styles.contentContainer}>
        {activeFilter === 'upcoming' && (
          <>
            <UpcomingReservationsList
              reservations={upcomingReservations}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCardClick={handleCardClick}
            />
            {counts.upcoming > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={counts.upcoming}
              />
            )}
          </>
        )}

        {activeFilter === 'past' && (
          <PastReservationsByMonth
            groupedReservations={groupedPastReservations}
            sortedMonthKeys={sortedMonthKeys}
            expandedMonths={expandedMonths}
            toggleMonth={toggleMonth}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCardClick={handleCardClick}
          />
        )}

        {activeFilter === 'all' && (
          <div className={styles.allReservationsView}>
            {counts.upcoming > 0 && (
              <div className={styles.sectionBlock}>
                <h3 className={styles.sectionTitle}>Próximas Reservas</h3>
                <UpcomingReservationsList
                  reservations={upcomingReservations}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCardClick={handleCardClick}
                />
              </div>
            )}

            {counts.past > 0 && (
              <div className={styles.sectionBlock}>
                <h3 className={styles.sectionTitle}>Reservas Pasadas</h3>
                <PastReservationsByMonth
                  groupedReservations={groupedPastReservations}
                  sortedMonthKeys={sortedMonthKeys}
                  expandedMonths={expandedMonths}
                  toggleMonth={toggleMonth}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCardClick={handleCardClick}
                />
              </div>
            )}

            {counts.all === 0 && (
              <div className={styles.emptyState}>
                <Calendar className={styles.emptyIcon} />
                <p className={styles.emptyMessage}>No hay reservas</p>
                <p className={styles.emptySubtext}>Tus reservas aparecerán aquí</p>
              </div>
            )}
          </div>
        )}
      </div>

      {editingReservation && (
        <EditReservationModal
          reservation={editingReservation}
          editForm={editForm}
          onEditFormChange={handleEditFormChange}
          onSaveEdit={handleSaveEdit}
          onClose={() => {
            setEditingReservation(null);
            setEditForm({});
            setConflictData(null);
          }}
          conflictData={conflictData}
          onDayToggle={handleDayToggle}
          onResolutionChange={handleResolutionChange}
          canDelegateReservations={canDelegateReservations}
        />
      )}

      {selectedReservation && (
        <ReservationDetailsModal
          reservation={selectedReservation}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};


export default MyReservations;